import React, { useState, useCallback } from 'react'
import { Autocomplete, Box, Checkbox, Paper, TextField, Typography, FormLabel, Chip } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CancelIcon from '@mui/icons-material/Cancel'
import { grey } from '../../../colors'
import { useTheme } from '@mui/material/styles'
import { Controller, useFormContext, useFormState } from 'react-hook-form'
import api from '../../../utils/api'
import { debounce } from 'lodash'
import { ReactComponent as SelectedCheckBox } from '../../../assets/check-box-selected.svg'
import { useQuery } from '@tanstack/react-query'
import { transformAttributesListForDropdown } from '../../../helpers/transformAttributesListForDropdown'
import { type IAttribute } from '../../../models/TermsSection'

const icon = <Box border={`1px solid ${grey[350]}`} borderRadius="4px" width="18px" height="18px" />

interface AttributesMultiSelectProps {
  name: string
  disabled?: boolean
  extraParams?: Record<string, any>
  required?: boolean
  defaultAttributes?: IAttribute[]
}

const AttributesMultiSelect: React.FC<AttributesMultiSelectProps> = ({
  name,
  disabled = false,
  extraParams = {},
  required = false,
  defaultAttributes
}) => {
  const theme = useTheme()
  const [input, setInput] = useState<string>('')

  // Debounced input handler
  const onInputChange = useCallback((value: string) => {
    setInput(value)
  }, [])

  const debouncedInputChange = useCallback(debounce(onInputChange, 1000), [onInputChange])

  const { data: attributesList, isLoading, isFetching } = useQuery<any[]>({
    queryKey: ['attributesList', name, input],
    queryFn: async (): Promise<any[]> => {
      const result = await api.get('/api/attributes/', {
        params: { pageSize: 10, search: input, ...extraParams }
      })
      return result.data
    },
    staleTime: 1000
  })

  const { control } = useFormContext()
  const { errors } = useFormState({ control, name })
  const errorMessage = errors?.[name]?.message ?? null

  const transformedAttributes = transformAttributesListForDropdown(attributesList ?? [])

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Box>
          <FormLabel required={required} sx={{ color: grey[700], fontSize: '14px' }}>
            Attributes
          </FormLabel>
          <Autocomplete
            loading={isFetching || isLoading}
            multiple
            size="small"
            value={field.value || []}
            onChange={(_, newValue) => { field.onChange(newValue) }}
            options={transformedAttributes ?? []}
            filterOptions={(options) => options}
            disableCloseOnSelect
            clearIcon={<CancelIcon sx={{ color: grey[950] }} />}
            getOptionLabel={(option) => option.title || option.value}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') debouncedInputChange(value)
            }}
            popupIcon={<ExpandMoreIcon sx={{ color: grey[150] }} />}
            placeholder="Instructor Name"
            sx={{ mt: theme.spacing(2) }}
            PaperComponent={({ children }) => (
              <Paper elevation={4} >
                {children}
              </Paper>
            )}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ width: '98%' }}>
                <Box display="flex">
                  <Checkbox
                    icon={icon}
                    checkedIcon={<SelectedCheckBox width="18px" height="18px" />}
                    checked={selected}
                    sx={{ mt: theme.spacing(-3.5) }}
                    disableRipple
                  />
                  <Box display="flex" flexDirection="column">
                    <Typography fontSize="14px" color={grey[250]} fontWeight={500}>
                      {option.value}
                    </Typography>
                    <Typography fontSize="14px" color={grey[850]}>
                      {option.title}
                    </Typography>
                  </Box>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                error={Boolean(errors[name])}
                helperText={errorMessage as string}
              />
            )}
            renderTags={(value: any, getTagProps) =>
              value?.map((option: any, index: number) => {
                const { key, ...tagProps } = getTagProps({ index })
                return (
                  <Chip
                    variant="filled"
                    label={option.value || option.title}
                    key={key}
                    {...tagProps}
                    disabled={option.isInherited}
                  />
                )
              })
            }
            disabled={disabled}
            getOptionDisabled={(option) =>
              defaultAttributes?.some(
                (val: IAttribute) => val.value === option.value && val.isInherited
              ) ?? false
            }
          />
        </Box>
      )}
    />
  )
}

export default AttributesMultiSelect
